import { FileWarning, Landmark, FolderX, LifeBuoy, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AQMark } from "@/components/app/Logo";
import { RiskCheck } from "@/components/app/RiskCheck";

const RISKS = [
  { icon: FileWarning, title: "Missed Filings", text: "Annual returns and state filings can trigger penalties and dissolution.", color: "#EF4444" },
  { icon: Landmark, title: "Tax Problems", text: "Late tax obligations create interest, penalties and compliance risk.", color: "#F97316" },
  { icon: FolderX, title: "Lost Records", text: "Missing corporate records create governance and audit issues.", color: "#F59E0B" },
  { icon: LifeBuoy, title: "Revival Costs", text: "Fixing a dissolved corporation is significantly more expensive than preventing the problem.", color: "#EF4444" },
];

const slug = (t) => t.toLowerCase().replace(/\s+/g, "-");

const RiskCard = ({ icon: Icon, title, text, color, i }) => (
  <div className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05] rise"
    style={{ animationDelay: `${i * 90}ms` }} data-testid={`risk-card-${slug(title)}`}>
    <span className="absolute inset-x-0 top-0 h-px opacity-70" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
    <div className="flex items-center justify-between">
      <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${color}1a`, color }}><Icon className="h-5 w-5" /></span>
      <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
    </div>
    <h3 className="mt-6 text-lg font-semibold text-white">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-400">{text}</p>
  </div>
);

export const RiskSection = () => (
  <section className="relative bg-[#0B0F19] text-slate-100 border-t border-white/[0.06]" data-testid="risk-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
      <div className="max-w-3xl">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-red-400/90 mb-3">The cost of inaction</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="risk-section-title">What Happens When You Ignore Corporate Health?</h2>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {RISKS.map((r, i) => <RiskCard key={r.title} {...r} i={i} />)}
      </div>
      <RiskCheck />
      <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-6 sm:p-7" data-testid="risk-section-conclusion">
        <AQMark size={40} testId="risk-section-mark" />
        <p className="flex-1 text-base sm:text-lg font-medium text-slate-100">
          AccruallQ helps you identify risks <span className="text-emerald-400">before they become expensive problems.</span>
        </p>
        <Link to="/register" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors" data-testid="risk-section-cta">
          Start free trial <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);
